import type {
  NewsLetterCampaignStatus,
  NewsLetterHistory,
  NewsLetterHistoryEntry,
  NewsLetterTemplateRecord,
  NewsLetterSubscriber,
} from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button, Card, ConfirmButton, Input, PageHeader, QueryState } from '@/components/ui';
import { SendNewsletterModal } from '@/components/send-newsletter-modal';
import { awaitingReviewRequest, findReviewTemplate } from '@/lib/newsletter-recipients';

/** What the send modal is currently opened for. */
type SendTarget = {
  template: NewsLetterTemplateRecord;
  initialSelection?: number[];
};

/**
 * Mirrors AdminNewsLetterService.replaceContent() on the API, purely for
 * visual preview — real sends always append the actual working link
 * server-side, this is never what gets mailed. Kept identical in wording so
 * what staff see while editing matches the real email; if that footer text
 * ever changes on the API, update it here too.
 */
const UNSUBSCRIBE_FOOTER_PREVIEW =
  '<br><br><p style="font-size:12px;color:#888888">Amennyiben nem szeretne több hírlevelet kapni, <a href="#">kattintson ide a leiratkozáshoz</a>.</p>';

/** Most email templates are laid out for ~600–650px; scale to that rather than the iframe's own. */
const EMAIL_PREVIEW_DESIGN_WIDTH = 650;
const EMAIL_PREVIEW_HEIGHT = 500;

/**
 * Renders the iframe at its natural email width and shrinks it with a CSS
 * transform to fit whatever space is actually available, rather than
 * letting fixed-width email markup (tables with `width="600"` etc., which
 * don't respond to the iframe's own CSS width) force a horizontal
 * scrollbar. Vertical overflow is left alone — deliberately not scaled,
 * the iframe's own vertical scrollbar handles a tall email fine.
 */
function EmailPreview({ srcDoc }: { srcDoc: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setScale(Math.min(1, entry.contentRect.width / EMAIL_PREVIEW_DESIGN_WIDTH));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className="overflow-hidden" style={{ height: EMAIL_PREVIEW_HEIGHT * scale }}>
      <iframe
        title="Hírlevél előnézet"
        srcDoc={srcDoc}
        sandbox=""
        style={{
          width: EMAIL_PREVIEW_DESIGN_WIDTH,
          height: EMAIL_PREVIEW_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          border: 0,
        }}
        className="bg-white"
      />
    </div>
  );
}

/**
 * A subscriber's send history. `enabled: expanded` is the lazy-loading part —
 * the request only fires the first time a row is opened, not for all 900+
 * subscribers up front; React Query then caches it for the rest of the visit.
 */
function SubscriberHistory({ subscriberId, expanded }: { subscriberId: number; expanded: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['newsletter-history', subscriberId],
    queryFn: () => api.get<NewsLetterHistory>(`/admin/newsletter/${subscriberId}/history`),
    enabled: expanded,
  });

  const statusLabel: Record<NewsLetterHistoryEntry['status'], string> = {
    sent: 'Elküldve',
    failed: 'Sikertelen',
    skipped: 'Kihagyva (leiratkozott)',
    pending: 'Folyamatban',
  };

  if (isLoading) return <p className="py-2 text-xs text-white/40">Betöltés…</p>;
  if (!data) return null;

  const { entries, legacySendCount } = data;

  if (entries.length === 0 && legacySendCount === 0) {
    return <p className="py-2 text-xs text-white/40">Még nem kapott hírlevelet.</p>;
  }

  return (
    <div className="py-2">
      {entries.length > 0 && (
        <ul className="space-y-1 text-xs">
          {entries.map((h) => (
            <li key={h.campaignId} className="flex items-center justify-between gap-3 text-white/70">
              <span className="truncate">{h.subject}</span>
              <span className="shrink-0 text-white/40">
                {h.sentAt ? new Date(h.sentAt).toLocaleDateString('hu-HU') : '—'} · {statusLabel[h.status]}
              </span>
            </li>
          ))}
        </ul>
      )}
      {legacySendCount > 0 && (
        // The old system only ever counted sends, never logged which
        // template or when — this isn't a bug, that detail simply doesn't
        // exist, so say so rather than show a suspiciously short list.
        <p className={`text-xs text-white/40 ${entries.length > 0 ? 'mt-2 border-t border-white/10 pt-2' : ''}`}>
          + {legacySendCount} korábbi küldés a részletes napló bevezetése előtt — ezekről nincs részletes adat.
        </p>
      )}
    </div>
  );
}

function Subscribers({ subscribers }: { subscribers: NewsLetterSubscriber[] }) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/newsletter/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['newsletter'] }),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-white/40">
          <tr className="border-b border-white/10">
            <th className="py-2 pr-3">Név</th>
            <th className="py-2 pr-3">Email</th>
            <th className="py-2 pr-3">Forrás</th>
            <th className="py-2 pr-3">Küldve</th>
            <th className="py-2 pr-3">Véleménykérő</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {subscribers.map((sub) => {
            const isExpanded = expandedId === sub.id;
            return (
              <>
                <tr key={sub.id} className="border-b border-white/5">
                  <td className="py-2 pr-3">{sub.name}</td>
                  <td className="py-2 pr-3 text-white/70">{sub.email}</td>
                  <td className="py-2 pr-3 text-white/50">{sub.reason}</td>
                  <td className="py-2 pr-3 text-white/50">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                      className="underline decoration-dotted underline-offset-2 hover:text-white"
                      title="Küldési előzmények"
                    >
                      {sub.sentMails}
                    </button>
                  </td>
                  {/* Read-only: this records what the system actually sent, so it is
                      no longer something a colleague has to remember to tick. */}
                  <td className="py-2 pr-3">
                    {sub.velemenykeroSent ? (
                      <span className="text-brand" title="Véleménykérő elküldve">
                        ✓
                      </span>
                    ) : (
                      <span className="text-white/25" title="Még nem kapott véleménykérőt">
                        —
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <ConfirmButton
                      onConfirm={() => remove.mutate(sub.id)}
                      confirmText={`Biztosan törli ${sub.email} feliratkozását?`}
                      className="!px-2 !py-1"
                    >
                      ×
                    </ConfirmButton>
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${sub.id}-history`} className="border-b border-white/5 bg-white/[0.02]">
                    <td colSpan={6} className="px-3">
                      <SubscriberHistory subscriberId={sub.id} expanded={isExpanded} />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Templates({
  onSend,
  reviewTemplateId,
}: {
  onSend: (template: NewsLetterTemplateRecord) => void;
  reviewTemplateId?: number;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<NewsLetterTemplateRecord> | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<NewsLetterTemplateRecord[]>('/admin/newsletter/template'),
  });

  const save = useMutation({
    mutationFn: (tpl: Partial<NewsLetterTemplateRecord>) =>
      tpl.id
        ? api.patch(`/admin/newsletter/template/${tpl.id}`, tpl)
        : api.post('/admin/newsletter/template', tpl),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/newsletter/template/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setEditing({ name: '', subject: '', content: '' })}>Új sablon</Button>
      </div>

      {editing && (
        <Card className="mb-4 p-4">
          <div className="grid gap-6 lg:grid-cols-2">
          <div className="grid gap-3">
            <Input
              placeholder="Sablon neve"
              value={editing.name ?? ''}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
            <Input
              placeholder="Tárgy"
              value={editing.subject ?? ''}
              onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
            />
            <textarea
              rows={8}
              placeholder="Tartalom (HTML)"
              value={editing.content ?? ''}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              className="w-full rounded-md border border-white/15 bg-white/[0.04] px-3 py-2 font-mono text-sm outline-none focus:border-brand"
            />
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={editing.isReviewRequest ?? false}
                onChange={(e) => setEditing({ ...editing, isReviewRequest: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
              />
              <span>
                Ez a véleménykérő sablon
                <span className="mt-0.5 block text-xs text-white/50">
                  Elküldése után a feliratkozó megjelölve marad, így nem kap még egyszer
                  véleménykérőt. Egyszerre csak egy sablon lehet ilyen.
                </span>
              </span>
            </label>
            <div className="flex gap-2">
              <Button onClick={() => save.mutate(editing)} disabled={save.isPending}>
                Mentés
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Mégse
              </Button>
            </div>
            {save.error && <p className="text-sm text-red-400">A mentés nem sikerült.</p>}
          </div>

          {/* Newsletter HTML is a standalone email, not a fragment styled by
              this page — often a full <html><style> document pasted from
              Mailchimp or similar. An iframe renders it in true isolation,
              so the preview matches what actually lands in an inbox instead
              of picking up (or clashing with) the admin's own Tailwind CSS. */}
          <div>
            <p className="text-sm text-white/80">Előnézet</p>
            <p className="mt-1 truncate text-xs text-white/50">{editing.subject || '(nincs tárgy)'}</p>
            <Card className="mt-1 overflow-hidden p-0">
              <EmailPreview srcDoc={(editing.content ?? '') + UNSUBSCRIBE_FOOTER_PREVIEW} />
            </Card>
          </div>
          </div>
        </Card>
      )}

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={(data?.length ?? 0) === 0}
        emptyText="Még nincs sablon."
      >
        <div className="space-y-3">
          {data?.map((tpl) => (
            <Card key={tpl.id} className="p-4" data-testid="template-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium">
                    {tpl.name}
                    {tpl.id === reviewTemplateId && (
                      <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-normal text-brand">
                        Véleménykérő
                      </span>
                    )}
                  </p>
                  <p className="mt-1 truncate text-sm text-white/60">{tpl.subject}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditing(tpl)}>
                    Szerkesztés
                  </Button>
                  {/* Always enabled: recipients are chosen in the modal, so
                      there is nothing to select beforehand. */}
                  <Button onClick={() => onSend(tpl)}>Küldés</Button>
                  <ConfirmButton
                    onConfirm={() => remove.mutate(tpl.id)}
                    confirmText={`Biztosan törli a(z) "${tpl.name}" sablont?`}
                  >
                    Törlés
                  </ConfirmButton>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </QueryState>
    </>
  );
}

function SendStatus() {
  const queryClient = useQueryClient();
  // A send can now span days (Resend free tier: 90/day, sent list is 900+),
  // so this polls slowly — nobody watches a single browser tab for a week;
  // it just needs to notice when today's batch has landed or the whole
  // campaign has finished.
  const { data } = useQuery({
    queryKey: ['send-status'],
    queryFn: () => api.get<NewsLetterCampaignStatus>('/admin/newsletter/send-status'),
    refetchInterval: (query) => (query.state.data?.inProgress ? 10000 : false),
  });

  const cancel = useMutation({
    mutationFn: () => api.delete('/admin/newsletter/send'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['send-status'] }),
  });

  // The server marks recipients as each daily batch runs, so the subscriber
  // table is stale until a batch lands. Refresh it whenever a batch just
  // went out (lastSentDate changed) or the campaign ends — otherwise the
  // Véleménykérő ticks only appear after a manual reload.
  const inProgress = data?.inProgress ?? false;
  const lastSentDate = data?.lastSentDate ?? null;
  const prev = useRef<{ inProgress: boolean; lastSentDate: string | null }>({
    inProgress: false,
    lastSentDate: null,
  });
  useEffect(() => {
    if ((prev.current.inProgress && !inProgress) || prev.current.lastSentDate !== lastSentDate) {
      queryClient.invalidateQueries({ queryKey: ['newsletter'] });
    }
    prev.current = { inProgress, lastSentDate };
  }, [inProgress, lastSentDate, queryClient]);

  if (!data || (!data.inProgress && data.total === 0)) return null;

  const done = data.sent + data.failed + data.skipped;
  const pct = data.total > 0 ? Math.round((done / data.total) * 100) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const sentToday = data.inProgress && data.lastSentDate === today;

  return (
    <Card className="mb-6 p-4">
      <div className="flex items-center justify-between text-sm">
        <span>
          {!data.inProgress
            ? 'Küldés befejezve'
            : sentToday
              ? `Mai adag elküldve (napi max. ${data.dailyBudget}) — folytatás holnap`
              : `Küldés folyamatban… (naponta max. ${data.dailyBudget})`}
        </span>
        <span className="flex items-center gap-3 text-white/60">
          <span>
            {done} / {data.total} · {data.sent} sikeres
            {data.failed > 0 && <span className="text-red-400"> · {data.failed} sikertelen</span>}
            {data.skipped > 0 && <span> · {data.skipped} leiratkozott</span>}
          </span>
          {data.inProgress && (
            <ConfirmButton
              onConfirm={() => cancel.mutate()}
              confirmText="Biztosan megszakítja a folyamatban lévő hírlevél küldését? A már elküldött leveleket ez nem vonja vissza."
              className="!px-2 !py-1"
            >
              Megszakítás
            </ConfirmButton>
          )}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
    </Card>
  );
}

export function NewsletterPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'subscribers' | 'templates'>('subscribers');
  const [sendTarget, setSendTarget] = useState<SendTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const subscribersQuery = useQuery({
    queryKey: ['newsletter'],
    queryFn: () => api.get<NewsLetterSubscriber[]>('/admin/newsletter'),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<NewsLetterTemplateRecord[]>('/admin/newsletter/template'),
  });

  const subscribers = subscribersQuery.data ?? [];
  const reviewTemplate = findReviewTemplate(templatesQuery.data ?? []);
  const notYetAsked = awaitingReviewRequest(subscribers);

  const send = useMutation({
    mutationFn: ({
      template,
      customers,
    }: {
      template: NewsLetterTemplateRecord;
      customers: NewsLetterSubscriber[];
    }) =>
      api.post<{ campaign: { id: number }; skippedAsDuplicate: number }>('/admin/newsletter/send', {
        template,
        customers,
      }),
    onSuccess: (result) => {
      setSendTarget(null);
      setError(null);
      // Anyone who already received this exact newsletter is excluded
      // automatically (see startNewsLetterProcess) — worth telling staff,
      // since the recipient count they confirmed and what actually queued
      // can now legitimately differ.
      setNotice(
        result.skippedAsDuplicate > 0
          ? `${result.skippedAsDuplicate} feliratkozó kimaradt, mert már megkapta ezt a hírlevelet korábban.`
          : null
      );
      queryClient.invalidateQueries({ queryKey: ['send-status'] });
      // The send loop marks recipients as it goes, so the table is stale the
      // moment sending starts.
      queryClient.invalidateQueries({ queryKey: ['newsletter'] });
    },
    onError: (e: unknown) =>
      setError(
        // The 409 case ("a send is already in progress") has a specific,
        // actionable Hungarian message from the server — show it rather
        // than a generic failure the user can't do anything about.
        e instanceof ApiError && e.status === 409 ? e.message : 'A küldés indítása nem sikerült.'
      ),
  });

  function confirmSend(template: NewsLetterTemplateRecord, recipients: NewsLetterSubscriber[]) {
    if (recipients.length === 0) return;

    // Sending real email to real customers is not undoable — confirm the count
    // explicitly. The Angular admin sent as soon as the button was clicked.
    const ok = window.confirm(
      `Biztosan elküldi a(z) "${template.name}" hírlevelet ${recipients.length} feliratkozónak? Ez nem vonható vissza.`
    );
    if (!ok) return;

    setError(null);
    send.mutate({ template, customers: recipients });
  }

  return (
    <>
      <PageHeader title="Hírlevél" />
      <SendStatus />

      {error && <p className="mb-4 text-sm text-red-400">{error}</p>}
      {notice && <p className="mb-4 text-sm text-white/60">{notice}</p>}

      <div className="mb-5 flex gap-1 border-b border-white/10">
        {(
          [
            ['subscribers', 'Feliratkozók'],
            ['templates', 'Sablonok'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
              tab === key
                ? 'border-brand text-brand'
                : 'border-transparent text-white/60 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'subscribers' ? (
        <QueryState
          isLoading={subscribersQuery.isLoading}
          error={subscribersQuery.error}
          isEmpty={subscribers.length === 0}
          emptyText="Még nincs feliratkozó."
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-white/40">
              {subscribers.length} feliratkozó · {notYetAsked.length} még nem kapott véleménykérőt
            </p>
            {reviewTemplate ? (
              <Button
                onClick={() =>
                  setSendTarget({
                    template: reviewTemplate,
                    initialSelection: notYetAsked.map((s) => s.id),
                  })
                }
                disabled={notYetAsked.length === 0}
              >
                Véleménykérő küldése ({notYetAsked.length})
              </Button>
            ) : (
              // Without a flagged template there is nothing to send and no way
              // to record it, so say which step is missing instead of showing a
              // dead button.
              <p className="text-xs text-white/40">
                Jelöljön meg egy sablont véleménykérőként a Sablonok fülön.
              </p>
            )}
          </div>

          <Subscribers subscribers={subscribers} />
        </QueryState>
      ) : (
        <Templates
          onSend={(template) => setSendTarget({ template })}
          reviewTemplateId={reviewTemplate?.id}
        />
      )}

      {sendTarget && (
        <SendNewsletterModal
          template={sendTarget.template}
          subscribers={subscribers}
          initialSelection={sendTarget.initialSelection}
          onCancel={() => setSendTarget(null)}
          onConfirm={(recipients) => confirmSend(sendTarget.template, recipients)}
          isSending={send.isPending}
        />
      )}
    </>
  );
}
